import Modal from "components/Elements/Modalv2";
import Button, { ButtonType } from "components/Elements/Buttonv2";
import Input from "components/Elements/Inputv2";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import ApiService from "services/api.service";
import Template from "types/Template";
import { toast } from "react-toastify";

// The full sequence the save endpoints return, enough for the list to render a
// row without refetching.
export interface SavedSequence {
  id: string;
  name: string;
  description: string | null;
  tags: string[];
  templates: { id: number; name: string; type: string }[];
}

interface SequenceBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: (saved: SavedSequence, mode: "create" | "edit") => void;
  /** When set, the modal opens in edit mode and prefills from the loaded sequence. */
  editingSequenceId?: string | null;
  /** Called when the user wants to jump out and create a new template (e.g. from the empty picker). */
  onRequestCreateTemplate?: () => void;
}

interface SequenceTemplateRow {
  id: number;
  name: string;
  type: string;
}

const SequenceBuilderModal: FC<SequenceBuilderModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  editingSequenceId,
  onRequestCreateTemplate,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [allTemplates, setAllTemplates] = useState<Template[]>([]);
  const [pickedIds, setPickedIds] = useState<number[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [knownTags, setKnownTags] = useState<string[]>([]);

  const loadKnownTags = async () => {
    try {
      const { data } = await ApiService.get<string[]>({
        url: `/sequences/tags`,
      });
      setKnownTags(Array.isArray(data) ? data : []);
    } catch {
      // Non-fatal — just no suggestions.
      setKnownTags([]);
    }
  };

  const loadTemplates = async () => {
    try {
      const {
        data: { data },
      } = await ApiService.get<{ data: Template[]; totalPages: number }>({
        url: `/templates?take=200&skip=0&search=&type=email&type=sms&type=slack&type=webhook&type=push&type=in-app`,
      });
      setAllTemplates(data);
    } catch {
      toast.error("Failed to load templates");
    }
  };

  const loadSequence = async (id: string) => {
    try {
      const { data } = await ApiService.get<{
        id: string;
        name: string;
        description: string | null;
        tags: string[];
        templates: SequenceTemplateRow[];
      }>({ url: `/sequences/${id}` });
      setName(data.name);
      setDescription(data.description ?? "");
      setTags(data.tags ?? []);
      setPickedIds(data.templates.map((t) => Number(t.id)));
    } catch {
      toast.error("Failed to load sequence");
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    if (!editingSequenceId) {
      setName("");
      setDescription("");
      setTags([]);
      setSearch("");
      setPickedIds([]);
    }
    void loadTemplates();
    void loadKnownTags();
    if (editingSequenceId) void loadSequence(editingSequenceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editingSequenceId]);

  const filteredTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allTemplates.filter(
      (t) =>
        !pickedIds.includes(Number(t.id)) &&
        (!q || t.name.toLowerCase().includes(q))
    );
  }, [allTemplates, pickedIds, search]);

  const pickedTemplates = useMemo(() => {
    const byId = new Map<number, Template>(
      allTemplates.map((t) => [Number(t.id), t])
    );
    return pickedIds
      .map((id) => byId.get(id))
      .filter((t): t is Template => !!t);
  }, [allTemplates, pickedIds]);

  const move = (idx: number, dir: -1 | 1) => {
    setPickedIds((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const remove = (idx: number) =>
    setPickedIds((prev) => prev.filter((_, i) => i !== idx));

  const onDragStart = (idx: number) => (e: React.DragEvent) => {
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = (idx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    if (dragIndex === null) return;
    e.dataTransfer.dropEffect = "move";
    if (dropIndex !== idx) setDropIndex(idx);
  };

  const onDrop = (idx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === idx) {
      setDragIndex(null);
      setDropIndex(null);
      return;
    }
    setPickedIds((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setDragIndex(null);
    setDropIndex(null);
  };

  const onDragEnd = () => {
    setDragIndex(null);
    setDropIndex(null);
  };

  const addTag = (value: string) => {
    if (tags.includes(value)) return;
    setTags([...tags, value]);
  };

  const removeTag = (t: string) => setTags(tags.filter((x) => x !== t));

  const tagSuggestions = useMemo(
    () => knownTags.filter((t) => !tags.includes(t)),
    [knownTags, tags]
  );

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = {
        name: name.trim(),
        description: description.trim() || undefined,
        tags,
        items: pickedIds.map((id, position) => ({
          templateId: String(id),
          position,
        })),
      };
      if (editingSequenceId) {
        const { data } = await ApiService.patch<SavedSequence>({
          url: `/sequences/${editingSequenceId}`,
          options: body,
        });
        return { data, mode: "edit" as const };
      }
      const { data } = await ApiService.post<SavedSequence>({
        url: `/sequences`,
        options: body,
      });
      return { data, mode: "create" as const };
    },
    onSuccess: ({ data, mode }) => {
      toast.success(mode === "edit" ? "Sequence updated" : "Sequence created");
      onSaved?.(data, mode);
      onClose();
    },
    onError: (e: any) => {
      toast.error(
        e?.response?.data?.message ||
          (editingSequenceId
            ? "Failed to update sequence"
            : "Failed to create sequence")
      );
    },
  });

  const submit = () => {
    if (!name.trim()) {
      toast.error("Sequence needs a name");
      return;
    }
    if (pickedIds.length === 0) {
      toast.error("Pick at least one template");
      return;
    }
    saveMutation.mutate();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      panelClassName="w-[1080px] max-w-[95vw] max-h-[90vh] overflow-y-auto px-[32px] pt-[32px] pb-[24px]"
    >
      <div className="flex flex-col gap-5 font-inter text-[14px] text-themed-text">
        <div className="text-[20px] font-semibold leading-[28px]">
          {editingSequenceId ? "Edit sequence" : "New sequence"}
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label className="font-medium">Name</label>
            <Input
              value={name}
              onChange={setName}
              placeholder="e.g. Welcome series"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="font-medium">Description (optional)</label>
            <Input
              value={description}
              onChange={setDescription}
              placeholder="What this sequence is for"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="font-medium">Tags</label>

            <div>
              <div className="text-[12px] text-themed-textSecondary mb-1">
                Selected
              </div>
              {tags.length === 0 ? (
                <div className="text-[12px] text-themed-textSecondary italic">
                  None — click a tag below to add it.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {tags.map((t) => (
                    <span
                      key={t}
                      className="px-2 py-[2px] rounded bg-[#6366F1] text-white text-[12px] flex items-center gap-1"
                    >
                      {t}
                      <button
                        onClick={() => removeTag(t)}
                        aria-label={`Remove ${t}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="text-[12px] text-themed-textSecondary mb-1">
                Available tags
              </div>
              {tagSuggestions.length === 0 ? (
                <div className="text-[12px] text-themed-textSecondary italic">
                  No tags configured for this workspace yet.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {tagSuggestions.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => addTag(t)}
                      className="px-2 py-[2px] rounded bg-themed-background text-[12px] border border-themed-border hover:border-[#6366F1]"
                    >
                      {t}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <div className="font-medium">Available templates</div>
            <Input
              value={search}
              onChange={setSearch}
              placeholder="Search templates"
            />
            <div className="border border-themed-border rounded h-[280px] overflow-y-auto">
              {allTemplates.length === 0 ? (
                <div className="p-3 flex flex-col gap-2 text-[13px]">
                  <div className="text-themed-textSecondary">
                    You don't have any templates yet. A sequence is built out of
                    templates, so make at least one first.
                  </div>
                  {onRequestCreateTemplate && (
                    <Button
                      type={ButtonType.SECONDARY}
                      onClick={() => {
                        onClose();
                        onRequestCreateTemplate();
                      }}
                    >
                      Create a template
                    </Button>
                  )}
                </div>
              ) : filteredTemplates.length === 0 ? (
                <div className="p-3 text-themed-textSecondary text-[13px]">
                  No templates match.
                </div>
              ) : (
                filteredTemplates.map((t) => (
                  <button
                    key={t.id}
                    className="w-full text-left px-3 py-2 hover:bg-themed-background flex items-center justify-between"
                    onClick={() => setPickedIds([...pickedIds, Number(t.id)])}
                  >
                    <span className="truncate pr-2">{t.name}</span>
                    <span className="text-[12px] text-themed-textSecondary">
                      {t.type}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 justify-end">
            <div className="flex items-baseline gap-2">
              <div className="font-medium">In this sequence (in order)</div>
              <div className="text-[12px] text-themed-textSecondary">
                {pickedTemplates.length === 0
                  ? "Nothing added yet"
                  : `${pickedTemplates.length} template${
                      pickedTemplates.length === 1 ? "" : "s"
                    } in order`}
              </div>
            </div>
            <div className="border border-themed-border rounded h-[280px] overflow-y-auto">
              {pickedTemplates.length === 0 ? (
                <div className="p-3 text-themed-textSecondary text-[13px]">
                  Click templates on the left to add them here.
                </div>
              ) : (
                pickedTemplates.map((t, idx) => (
                  <div
                    key={`${t.id}-${idx}`}
                    draggable
                    onDragStart={onDragStart(idx)}
                    onDragOver={onDragOver(idx)}
                    onDrop={onDrop(idx)}
                    onDragEnd={onDragEnd}
                    className={`flex items-center justify-between px-3 py-2 border-b border-themed-border last:border-b-0 cursor-grab active:cursor-grabbing ${
                      dragIndex === idx ? "opacity-40" : ""
                    } ${
                      dropIndex === idx && dragIndex !== idx
                        ? "bg-themed-background"
                        : ""
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="text-themed-textSecondary select-none"
                        aria-hidden
                      >
                        ⋮⋮
                      </span>
                      <span className="text-themed-textSecondary text-[12px] w-6">
                        {idx + 1}.
                      </span>
                      <span className="truncate">{t.name}</span>
                      <span className="text-[12px] text-themed-textSecondary">
                        ({t.type})
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => move(idx, -1)}
                        disabled={idx === 0}
                        className="px-1 disabled:opacity-30"
                        aria-label="Move up"
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => move(idx, 1)}
                        disabled={idx === pickedTemplates.length - 1}
                        className="px-1 disabled:opacity-30"
                        aria-label="Move down"
                      >
                        ↓
                      </button>
                      <button
                        onClick={() => remove(idx)}
                        className="px-1 text-[#F43F5E]"
                        aria-label="Remove"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button type={ButtonType.SECONDARY} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type={ButtonType.PRIMARY}
            onClick={submit}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending
              ? "Saving..."
              : editingSequenceId
              ? "Save sequence"
              : "Create sequence"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SequenceBuilderModal;
